<template>
  <div>
    <!-- MODO NORMAL: exibe seletor de refil + datas + botão -->
    <div v-if="!printMode">
      <h6>Análise de Receitas por Refil</h6>
      <div class="row mb-3">
        <!-- Seletor de Refil -->
        <div class="col-auto">
          <label>Refil:</label>
          <select v-model="refilLocal" class="form-control">
            <option value="" disabled>Selecione um refil</option>
            <option
              v-for="opt in refisOptions"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </select>
        </div>

        <!-- Data Início -->
        <div class="col-auto">
          <label>Data Início:</label>
          <input type="date" v-model="dataInicioLocal" class="form-control" />
        </div>

        <!-- Data Fim -->
        <div class="col-auto">
          <label>Data Fim:</label>
          <input type="date" v-model="dataFimLocal" class="form-control" />
        </div>

        <!-- Botão Executar -->
        <div class="col-auto align-self-end">
          <button @click="abrirPrint" class="btn btn-primary">
            Executar
          </button>
        </div>
      </div>
    </div>

    <!-- MODO IMPRESSÃO: busca dados no backend e exibe tabela -->
    <div v-else>
      <div v-if="loading">Carregando…</div>

      <div v-else-if="resultados.length">
        <p class="text-center" style="font-size: 16px">
          {{ subtitle }}
        </p>
        <table class="table table-striped">
          <thead>
            <tr>
              <th>DESCRIÇÃO</th>
              <th class="valor-cell">VALOR</th>
              <th class="text-center">FORMA DE PAGAMENTO</th>
              <th class="text-center">DATA VENCIMENTO</th>
              <th class="text-center">DATA PAGAMENTO</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in resultados" :key="index">
              <td>{{ item.descricao }}</td>
              <td class="valor-cell">{{ formatarMoeda(item.valor) }}</td>
              <td class="text-center">{{ item.forma_pagamento }}</td>
              <td class="text-center">{{ globalUtils.formatarData(item.data_vencimento) }}</td>
              <td class="text-center">{{ globalUtils.formatarData(item.data_pagamento) }}</td>
            </tr>
          </tbody>
          <!-- tfoot: exibe somente se total > 0 -->
          <tfoot v-if="total > 0">
            <tr>
              <td colspan="2"><strong>Total</strong></td>
              <td class="valor-cell text-right"><strong>{{ formatarMoeda(total) }}</strong></td>
              <td colspan="3"></td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div v-else>
        <p>Nenhum registro encontrado para este refil no período.</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import axios from '@/services/axios'
import globalUtils from '@/utils/globalUtils.js'

const props = defineProps({
  printMode:  { type: Boolean, default: false },
  dataInicio: String,
  dataFim:    String,
  refil:      [String, Number],
  printTitle: { type: String, default: 'Análise de Receitas por Refil' },
  subtitle:   { type: String, default: '' }
})

const router = useRouter()

// --- ESTADOS PARA MODO NORMAL ---
const refisOptions      = ref([])
const refilLocal        = ref('')
const dataInicioLocal   = ref('')
const dataFimLocal      = ref('')

// --- ESTADOS PARA MODO IMPRESSÃO ---
const loading    = ref(false)
const resultados = ref([])
const total      = ref(0)

onMounted(async () => {
  if (!props.printMode) {
    try {
      // Carrega subcontas de receitas gerais
      const res = await axios.get('/gestao/relatorios/receitas/subcontas-receitas')
      const listaSub = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data.data)
          ? res.data.data
          : []
      const opSub = listaSub.map(sc => ({ label: sc.nome, value: sc.id }))

      // Tipos fixos de receita
      const tiposFixos = [
        { label: 'Matrícula', value: 'Matrícula' },
        { label: 'Mensalidade', value: 'Mensalidade' },
        { label: 'Aula Avulsa', value: 'Aula Avulsa' },
        { label: 'Aula Experimental', value: 'Aula Experimental' },
        { label: 'Vendas da Loja', value: 'Venda da Loja' }
      ]

      refisOptions.value = [...tiposFixos, ...opSub]
    } catch (err) {
      console.error('Erro ao carregar opções de refil para receitas:', err)
      refisOptions.value = [
        { label: 'Matrícula', value: 'Matrícula' },
        { label: 'Mensalidade', value: 'Mensalidade' },
        { label: 'Aula Avulsa', value: 'Aula Avulsa' },
        { label: 'Aula Experimental', value: 'Aula Experimental' },
        { label: 'Vendas da Loja', value: 'Venda da Loja' }
      ]
    }
    return
  }

  if (!props.dataInicio || !props.dataFim || !props.refil) {
    console.warn('[ReceitasPorRefil] Faltam props dataInicio/dataFim/refil')
    return
  }

  loading.value = true
  try {
    const res = await axios.get('/gestao/relatorios/receitas/receitas-por-refil', {
      params: {
        refil: props.refil,
        dataInicio: props.dataInicio,
        dataFim: props.dataFim
      }
    })
    resultados.value = Array.isArray(res.data.items) ? res.data.items : []
    total.value      = parseFloat(res.data.total) || 0
  } catch (err) {
    console.error('Erro ao buscar receitas por refil:', err)
    resultados.value = []
    total.value      = 0
  } finally {
    loading.value = false
  }
})

function abrirPrint() {
  if (!refilLocal.value || !dataInicioLocal.value || !dataFimLocal.value) return

  const selected = refisOptions.value.find(
    opt => String(opt.value) === String(refilLocal.value)
  )
  const labelRefil = selected ? selected.label : '—'
  const inicioFmt  = globalUtils.formatarData(dataInicioLocal.value)
  const fimFmt     = globalUtils.formatarData(dataFimLocal.value)
  const subtitleLocal = `${labelRefil} - período de ${inicioFmt} a ${fimFmt}`

  router.push({
    path: '/gestao/relatorios/print/receitas-por-refil',
    query: {
      title:      props.printTitle,
      refil:      refilLocal.value,
      dataInicio: dataInicioLocal.value,
      dataFim:    dataFimLocal.value,
      subtitle:   subtitleLocal
    }
  })
}

function formatarMoeda(num) {
  return num == null
    ? ''
    : Number(num).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      })
}
</script>

<style scoped>
.mb-3 {
  margin-bottom: 1rem;
}
.valor-cell {
  text-align: right !important;
}
</style>
